import { AppConfig } from 'AppConfig';
import axios from 'axios';
import { POPULATION_MODE } from 'common/Constant';
import { calculateTimeWindowDates } from 'utils/timeWindowUtil';
import { defaultAppliedFilterValues, PRIOR_COMPARISON_DISABLED_DATE } from './globalFiltersDefaults';
import { normalizeGlobalFilterOptionLabels } from './globalFilterOptionUtils';
import { setDefaultsSeeded, setGlobalFilters } from './globalFiltersReducer';
import { getDefaultAppliedFilters, saveFiltersToStorage } from './globalFiltersStorage';

// The applied-filter fields that are persisted to the backend per user/tenant.
const PERSISTABLE_FILTER_KEYS = Object.keys(getDefaultAppliedFilters());

/**
 * Picks only the persistable applied-filter fields from the given applied state,
 * producing the payload stored on the backend. The backend normalizes the
 * population fields (cohort vs person id) based on `populationMode`.
 */
const buildSavedFilterPayload = (applied = {}) =>
  PERSISTABLE_FILTER_KEYS.reduce((payload, key) => {
    if (Object.prototype.hasOwnProperty.call(applied, key)) {
      payload[key] = applied[key];
    }
    return payload;
  }, {});

/**
 * Maps recalculated named-window dates onto the applied prior-period fields.
 *
 * calculateTimeWindowDates returns blank prior dates when the prior window has no
 * data. Every downstream consumer (metrics, Tableau, performance cards) keys off
 * `enablePriorComparison === false` plus the disabled sentinel to know the
 * comparison is off, so blank dates with the flag left true would send requests
 * with no prior bounds at all. Turn the comparison off explicitly in that case.
 */
const toAppliedPriorPeriod = (periodDates) => (
  periodDates.priorPeriodFrom && periodDates.priorPeriodTo
    ? {
      priorPeriodFrom: periodDates.priorPeriodFrom,
      priorPeriodTo: periodDates.priorPeriodTo
    }
    : {
      priorPeriodFrom: PRIOR_COMPARISON_DISABLED_DATE,
      priorPeriodTo: PRIOR_COMPARISON_DISABLED_DATE,
      enablePriorComparison: false
    }
);

/**
 * Re-derives the comma-separated person id list for a cohort by fetching the
 * cohort's current members. Returns an empty string on failure.
 */
const derivePersonIdsForCohort = async (tenantCd, luid) => {
  try {
    const response = await axios.post(
      AppConfig.FO_INITIAL_URL + '/cohorts/person-ids',
      { tenantCd, luid },
      { hideSpinner: true }
    );
    const personIds = Array.isArray(response?.data?.personIds) ? response.data.personIds : [];
    return personIds.join(',');
  } catch (error) {
    console.error('[GlobalFilters] Failed to derive cohort person ids:', error?.message || error);
    return '';
  }
};

/**
 * Persists the currently applied global filters for the given tenant so they can
 * be restored when the user returns to that tenant. Called on every Apply.
 *
 * The tenant is passed explicitly (rather than relying on the JWT) so a save that
 * coincides with a tenant switch is not affected by token-refresh timing.
 */
export const saveGlobalFiltersToBackend = (tenantCd, applied) => async () => {
  if (!tenantCd || !applied) {
    return;
  }

  try {
    await axios.put(
      AppConfig.FO_INITIAL_URL + '/global-filter/saved',
      { tenantCd, filter: buildSavedFilterPayload(applied) },
      { hideSpinner: true }
    );
  } catch (error) {
    console.error('[GlobalFilters] Failed to save filters to backend:', error?.message || error);
  }
};

/**
 * Fetches the saved global filter for the given tenant from the backend.
 *
 * Returns the saved filter object when one exists and has not expired (the
 * backend enforces the 30-day retention window), or null otherwise. This is a
 * plain async helper (not a thunk) so it can be awaited directly by components
 * that need to preview a saved filter without committing it to the store.
 */
export const fetchSavedFilter = async (tenantCd) => {
  if (!tenantCd) {
    return null;
  }

  try {
    const response = await axios.get(AppConfig.FO_INITIAL_URL + '/global-filter/saved', {
      params: { tenantCd },
      hideSpinner: true
    });
    if (!response?.data?.found || !response.data.filter) {
      return null;
    }
    return response.data.filter;
  } catch (error) {
    console.error('[GlobalFilters] Failed to load saved filters:', error?.message || error);
    return null;
  }
};

/**
 * Loads the saved global filter for the given tenant from the backend and, when
 * present and not expired, applies it as the active filter. For cohort-based
 * population filtering the person ids are re-derived from the cohort.
 *
 * Returns true when a saved filter was applied, false when none exists (or it has
 * expired), so the caller can fall back to default filter seeding.
 */
export const loadSavedFilters = async (dispatch, tenantCd) => {
  const savedFilter = await fetchSavedFilter(tenantCd);
  if (!savedFilter) {
    return false;
  }

  const applied = {
    ...savedFilter,
    enablePriorComparison: savedFilter.enablePriorComparison !== false,
  };

  if (savedFilter.enablePriorComparison === false) {
    applied.priorPeriodFrom = PRIOR_COMPARISON_DISABLED_DATE;
    applied.priorPeriodTo = PRIOR_COMPARISON_DISABLED_DATE;
  }

  const savedDateBasis = savedFilter.dateBasis ?? defaultAppliedFilterValues.dateBasis;
  const savedTimeWindow = savedFilter.timeWindow ?? defaultAppliedFilterValues.timeWindow;
  applied.dateBasis = savedDateBasis;
  applied.timeWindow = savedTimeWindow;

  // For named (non-custom) windows, re-derive dates so R12/PYTD stays current.
  if (savedTimeWindow !== 'CUSTOM') {
    try {
      const datesResponse = await axios.post(
        AppConfig.FO_INITIAL_URL + '/global-filter/available-dates',
        { dateBasis: savedDateBasis, tenantCd },
        { hideSpinner: true }
      );

      const periodDates = calculateTimeWindowDates(
        savedTimeWindow,
        datesResponse?.data?.latestDate,
        datesResponse?.data?.planStartDate,
        datesResponse?.data?.planYearDateRanges,
        datesResponse?.data?.oldestDate
      );

      if (periodDates) {
        applied.currentPeriodFrom = periodDates.currentPeriodFrom;
        applied.currentPeriodTo = periodDates.currentPeriodTo;
        // Only the applied copy is adjusted; the stored filter keeps the user's
        // preference so the comparison comes back once prior data exists.
        if (savedFilter.enablePriorComparison !== false) {
          Object.assign(applied, toAppliedPriorPeriod(periodDates));
        }
      }
    } catch (error) {
      console.error('[GlobalFilters] Failed to refresh saved filter dates:', error?.message || error);
    }
  }

  // Cohort population: re-derive the person ids from the cohort's current members.
  if (savedFilter.populationMode === POPULATION_MODE.COHORT && savedFilter.cohortLuid) {
    applied.personIdValue = await derivePersonIdsForCohort(tenantCd, savedFilter.cohortLuid);
  }
  dispatch(setGlobalFilters(applied));
  // Keep the last-applied cache in sync so new tabs inherit this filter.
  saveFiltersToStorage(applied);
  return true;
};

/**
 * Returns true when the applied filters still match the static defaults,
 * i.e. the user has not applied any custom filter selections yet.
 */
const isDefaultApplied = (applied) => {
  const defaults = getDefaultAppliedFilters();
  return Object.keys(defaults).every((key) => {
    const appliedValue = applied?.[key];
    const defaultValue = defaults[key];
    if (Array.isArray(defaultValue)) {
      return (
        Array.isArray(appliedValue) &&
        appliedValue.length === defaultValue.length &&
        appliedValue.every((value, index) => value === defaultValue[index])
      );
    }
    return appliedValue === defaultValue;
  });
};

/**
 * Converts an "MM/YYYY" period value to the "MM/01/YYYY" month-start format
 * expected by the `/global-filter/options` endpoint. Returns null when invalid.
 */
const monthYearToMonthStart = (value) => {
  if (value === PRIOR_COMPARISON_DISABLED_DATE) {
    return PRIOR_COMPARISON_DISABLED_DATE;
  }
  const match = /^(0[1-9]|1[0-2])\/(\d{4})$/.exec(String(value || '').trim());
  return match ? `${match[1]}/01/${match[2]}` : null;
};

/**
 * Builds the `/global-filter/options` request payload from the currently applied
 * filters (tenant, date basis and the seeded date range).
 */
const buildOptionsPayload = (tenantCd, applied) => {
  const currentStart = monthYearToMonthStart(applied?.currentPeriodFrom);
  const currentEnd = monthYearToMonthStart(applied?.currentPeriodTo);
  const includePriorComparison = applied?.enablePriorComparison !== false;
  const priorStart = includePriorComparison ? monthYearToMonthStart(applied?.priorPeriodFrom) : PRIOR_COMPARISON_DISABLED_DATE;
  const priorEnd = includePriorComparison ? monthYearToMonthStart(applied?.priorPeriodTo) : PRIOR_COMPARISON_DISABLED_DATE;

  return {
    tenantCd,
    dateBasis: applied?.dateBasis ?? defaultAppliedFilterValues.dateBasis,
    ...(currentStart ? { currentStart } : {}),
    ...(currentEnd ? { currentEnd } : {}),
    ...(priorStart ? { priorStart } : {}),
    ...(priorEnd ? { priorEnd } : {})
  };
};

/**
 * Returns true when the current tenant has global filters enabled. Filters are
 * considered disabled only when the company record explicitly sets
 * `filtersEnabled === false`.
 */
const currentTenantFiltersEnabled = (user) => {
  const currentTenant = user?.companies?.find((company) => company.code === user?.currentTenantCd);
  return currentTenant?.filtersEnabled !== false;
};

/**
 * Fetches the available division/plan option labels for the given tenant and
 * applied filters (date basis + date range) from `/global-filter/options`.
 *
 * Returns the full, normalized label lists (sorted, deduped). On failure or when
 * no tenant is provided, returns empty arrays.
 *
 * @returns {Promise<{ divisionLabels: string[], planLabels: string[] }>}
 */
export const fetchDefaultOptionLabels = async (tenantCd, applied) => {
  if (!tenantCd) {
    return { divisionLabels: [], planLabels: [] };
  }

  try {
    const optionsResponse = await axios.post(
      AppConfig.FO_INITIAL_URL + '/global-filter/options',
      buildOptionsPayload(tenantCd, applied),
      { hideSpinner: true }
    );

    const filterOptions = optionsResponse?.data?.filterOptions || {};
    return {
      divisionLabels: normalizeGlobalFilterOptionLabels(filterOptions.division),
      planLabels: normalizeGlobalFilterOptionLabels(filterOptions.plan)
    };
  } catch (error) {
    console.error('[GlobalFilters] Failed to fetch default option labels:', error?.message || error);
    return { divisionLabels: [], planLabels: [] };
  }
};

/**
 * Seeds the default global filters for the current tenant, in order:
 *   1. Resolve the rolling-12-months date range from `/global-filter/available-dates`
 *      (dateBasis = "paid") and set the current/prior period dates.
 *   2. Fetch the available division/plan options from `/global-filter/options`
 *      using the previously set filters (tenant, date basis, seeded dates), and
 *      set `divisionLabels` / `planLabels` from the response.
 *
 * Backend restoration runs for every enabled tenant initialization. Generated
 * defaults only run when no saved filter exists and the user has not already
 * applied custom filters, so existing selections are preserved and
 * filters-disabled tenants get no filter values at all. The `defaultsSeeded`
 * gate flag is set to true on every exit path (no-op, success and error) so
 * metrics consumers only load performance data once filters are in place.
 */
export const seedDefaultFilters = () => async (dispatch, getState) => {
  const state = getState();
  const tenantCd = state.user?.currentTenantCd;

  // Nothing to seed when there is no tenant or the tenant has filters disabled.
  if (
    !tenantCd ||
    !currentTenantFiltersEnabled(state.user)
  ) {
    dispatch(setDefaultsSeeded(true));
    return;
  }

  // Begin seeding: gate metrics consumers until defaults are fully resolved.
  dispatch(setDefaultsSeeded(false));

  // Prefer a previously saved filter for this tenant (the database is the source
  // of truth). When one exists and has not expired it is applied as-is.
  const savedFilterApplied = await loadSavedFilters(dispatch, tenantCd);
  if (savedFilterApplied) {
    dispatch(setDefaultsSeeded(true));
    return;
  }

  // No backend record exists. Preserve any custom filters already held by this
  // session instead of replacing them with generated defaults.
  if (!isDefaultApplied(state.globalFilters?.applied)) {
    dispatch(setDefaultsSeeded(true));
    return;
  }

  try {
    // 1. Resolve and apply the rolling-12-months date range.
    const datesResponse = await axios.post(
      AppConfig.FO_INITIAL_URL + '/global-filter/available-dates',
      { dateBasis: defaultAppliedFilterValues.dateBasis, tenantCd },
      { hideSpinner: true }
    );

    const periodDates = calculateTimeWindowDates(
      defaultAppliedFilterValues.timeWindow,
      datesResponse?.data?.latestDate,
      datesResponse?.data?.planStartDate,
      datesResponse?.data?.planYearDateRanges,
      datesResponse?.data?.oldestDate
    );

    if (periodDates) {
      dispatch(
        setGlobalFilters({
          currentPeriodFrom: periodDates.currentPeriodFrom,
          currentPeriodTo: periodDates.currentPeriodTo,
          ...toAppliedPriorPeriod(periodDates)
        })
      );
    }

    // 2. Fetch division/plan options using the previously set filters and
    //    select all of them by default.
    const appliedAfterDates = getState().globalFilters?.applied;
    const { divisionLabels, planLabels } = await fetchDefaultOptionLabels(tenantCd, appliedAfterDates);
    dispatch(
      setGlobalFilters({
        divisionLabels,
        planLabels,
        // The full option list is selected by default, so keep the drawer's
        // "All" checkboxes checked rather than showing a partial selection.
        isAllDivisionsSelected: true,
        isAllPlansSelected: true
      })
    );
  } catch (error) {
    console.error('[GlobalFilters] Failed to seed default filters:', error?.message || error);
  } finally {
    dispatch(setDefaultsSeeded(true));
  }
};